import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, ExternalLink, Image as ImageIcon, Users, Building2, FileText, Sparkles } from 'lucide-react';

export default function PropertiesPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/properties').then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="properties-title">Resort catalog</h1>
        <p className="text-xs text-muted-foreground">{t('propertiesPage.subtitle', 'I resort HCF Property — acquisto immobiliare (Real Estate).')}</p>
      </div>
      {loading && <div className="text-muted-foreground">{t('propertiesPage.loading', 'Caricamento…')}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => {
          const apAv = p.apartments?.available || 0;
          const apPrice = p.apartments?.min_anticipo_eur;
          return (
            <Card key={p.id} className="border-border overflow-hidden flex flex-col" data-testid="property-card">
              <div
                className="aspect-[16/10] w-full bg-[var(--surface-2)] cursor-pointer relative"
                style={{ backgroundImage: `url(${p.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                onClick={() => window.open(`/resort/${p.slug}`, '_blank')}
                data-testid="property-image"
              >
                <span className="absolute top-3 left-3 text-[10px] tracking-widest uppercase bg-white/95 text-[hsl(var(--primary))] rounded-full px-2 py-0.5 font-medium">Green Paradise</span>
              </div>
              <CardContent className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" /> {p.location}
                </div>
                <div className="font-serif text-xl text-[hsl(var(--primary))] mt-1">{p.name}</div>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.tagline || p.description}</p>

                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  {apAv > 0 && (
                    <div className="rounded-md border border-border p-2 bg-[#EFF8F8]">
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#0E5A5A]"><Building2 className="w-3 h-3" />Real Estate</div>
                      <div className="font-medium">{apAv} {t('propertiesPage.apartments', 'appartamenti')}</div>
                      {apPrice && <div className="text-muted-foreground">{t('propertiesPage.depositFrom', 'anticipo da')} €{(apPrice/1000).toFixed(1)}k</div>}
                    </div>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {(p.benefits || []).slice(0, 3).map((h) => (
                    <span key={h} className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--surface-2)] border border-border">{h}</span>
                  ))}
                </div>

                <div className="mt-4 flex gap-2 mt-auto pt-3">
                  <a href={`/resort/${p.slug}`} target="_blank" rel="noreferrer">
                    <Button variant="secondary" size="sm" data-testid="property-public-page">{t('propertiesPage.publicPage', 'Pagina pubblica')} <ExternalLink className="w-3 h-3 ml-1" /></Button>
                  </a>
                  {p.brochure_url && (
                    <a href={p.brochure_url} target="_blank" rel="noreferrer">
                      <Button variant="default" size="sm" data-testid="property-brochure-button"><FileText className="w-3 h-3 mr-1" /> Brochure</Button>
                    </a>
                  )}
                  {p.financing_url && (
                    <a href={p.financing_url} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="sm" data-testid="property-financing-button"><FileText className="w-3 h-3 mr-1" /> Financing</Button>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
